/**
 * Helper isolado pro filtro de departamentos da Sidebar (BUG-004 / B-FINAL.6.2).
 *
 * Extraído de Sidebar.tsx pra cumprir react-refresh rule (componentes JSX
 * em arquivo separado de helpers puros) + facilitar tests.
 *
 * Regras:
 *   - super-admin: vê TODOS (bypass total)
 *   - user sem dept: fail-open (vê todos — previne UX trancada)
 *   - user com dept(s): filtra por slug match
 *
 * NÃO REVERTER: super-admin SEMPRE vê tudo; fail-open intencional pra
 * usuários legacy sem dept atribuído (compat com seed antigo).
 */

export interface DeptItem {
  slug: string;
  to: string;
  label: string;
  Icon: unknown;
}

export function filterDepartamentosPorUser<T extends { slug: string }>(
  departamentos: T[],
  userDepartments: string[] | undefined,
  isSuperAdmin: boolean,
): T[] {
  if (isSuperAdmin) return departamentos;
  if (!userDepartments || userDepartments.length === 0) return departamentos;
  return departamentos.filter((d) => userDepartments.includes(d.slug));
}

/**
 * S-4 (SEGURANCA-ACESSO-2) — quando a tela TEM chave, quem manda no menu é a
 * permissão calculada, não o departamento.
 *
 * O menu e o porteiro discordavam. O item de topo saía de
 * `filterDepartamentosPorUser` (departamento do vínculo), enquanto abrir a tela
 * dependia da chave configurada em Permissões. Resultado nos dois sentidos:
 *   • quem recebeu `financeiro.ver` por exceção pessoal, sem estar no setor
 *     financeiro, PODIA abrir o Financeiro e não via o item para clicar;
 *   • quem está no setor financeiro sem a chave via o item e levava "Acesso
 *     restrito" na cara — menu mentindo.
 *
 * Rota SEM chave mapeada continua decidida pelo departamento, exatamente como
 * hoje: enquanto Clientes, Manutenção e Projetos não tiverem chave própria,
 * mudar o critério deles aqui tiraria o menu de gente que trabalha.
 *
 * Enquanto os acessos chegam (e para admin, que passa por tudo), mantemos a
 * decisão de hoje — sem isso o menu piscaria itens sumindo a cada carregamento.
 */
export function decidirDepartamentosVisiveis<T extends { slug: string; to: string }>(params: {
  /** O catálogo inteiro (para reencontrar item que o departamento escondeu). */
  todos: T[];
  /** O que o critério de departamento devolve hoje. */
  visiveisPorDepartamento: T[];
  chaveDaRota: (to: string) => string | null;
  temChave: (chave: string) => boolean;
  acessosCarregando: boolean;
  isAdmin: boolean;
}): T[] {
  const { todos, visiveisPorDepartamento, chaveDaRota, temChave, acessosCarregando, isAdmin } =
    params;
  const porDepartamento = new Set(visiveisPorDepartamento.map((d) => d.slug));
  return todos.filter((d) => {
    const chave = chaveDaRota(d.to);
    if (chave === null) return porDepartamento.has(d.slug);
    if (isAdmin || acessosCarregando) return porDepartamento.has(d.slug);
    return temChave(chave);
  });
}
